'use client';
import { useTranslations } from 'next-intl';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { VideoHoverCard } from '@/app/components/shared/VideoHoverCard/VideoHoverCard';
import { SITE } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { clawsListQuickPrompts } from '../../../../apis';
import type { QuickPrompt } from '../../../../types';

const SKELETON_KEYS = Array.from({ length: 6 }, (_, index) => `gameplay-skeleton-${index}`);

type WelcomeGameplayCasesProps = {
  onSelect?: (item: QuickPrompt) => void;
};

const getQuickPromptKey = (item: QuickPrompt, index: number) => item.id ?? item.label ?? `quick-prompt-${index}`;

export const WelcomeGameplayCases = ({ onSelect }: WelcomeGameplayCasesProps) => {
  const t = useTranslations();
  const { data, loading } = useFetch(clawsListQuickPrompts);
  const prompts = data?.data?.prompts ?? [];
  if (!loading && prompts.length === 0) return null;
  return (
    <div className="w-full">
      <div className="text-sm text-system-white36">{t('claw.chat.welcome.gameplayCases')}</div>
      <div className="mt-3 grid grid-cols-3 gap-4">
        {loading
          ? SKELETON_KEYS.map((skeletonKey) => (
              <div key={skeletonKey} className="aspect-video animate-pulse rounded-12 bg-system-white08" aria-hidden />
            ))
          : prompts.map((item, index) => {
              const key = getQuickPromptKey(item, index);
              return (
                <VideoHoverCard
                  key={key}
                  coverUrl={item.cover_url}
                  videoUrl={item.video_url}
                  alt={item.label ?? ''}
                  className="rounded-12 border border-system-white08 bg-system-black36 light:bg-[rgba(18,24,38,0.04)]"
                  objectFit="contain"
                  onClick={() => {
                    reportEvent('claw_element_click', {
                      element_name: 'quick_cases_prompt_click',
                      case_name: item.label ?? '',
                    });
                    onSelect?.(item);
                  }}
                >
                  {SITE === 'china' && <MediaWithWatermark overlay width={16} height={9} showWatermark />}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <div className="line-clamp-1 text-white text-xs">{item.label}</div>
                  </div>
                </VideoHoverCard>
              );
            })}
      </div>
      {!loading && prompts.length > 0 && (
        <div className="mt-4 text-center text-sm text-system-white36">{t('claw.chat.welcome.noMoreCases')}</div>
      )}
    </div>
  );
};
